import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, RotateCcw, Check, Moon, Sun, Palette, Sliders } from 'lucide-react';
import { useUIStore, applyCustomVars, type CustomVars, type Theme } from '../../stores/uiStore';

// ─── Helpers ──────────────────────────────────────────────────────────────
function hexToRgb(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  if ([r, g, b].some(isNaN)) return '14, 165, 233';
  return `${r}, ${g}, ${b}`;
}
function rgbToHex(rgb: string): string {
  if (!rgb) return '#0ea5e9';
  const parts = rgb.split(',').map((s) => parseInt(s.trim(), 10));
  if (parts.length !== 3 || parts.some(isNaN)) return '#0ea5e9';
  return '#' + parts.map((x) => Math.max(0, Math.min(255, x)).toString(16).padStart(2, '0')).join('');
}
function asHexFromRgba(rgba?: string): string | undefined {
  if (!rgba) return undefined;
  const m = rgba.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (!m) return rgba.startsWith('#') ? rgba : undefined;
  const [r, g, b] = [m[1], m[2], m[3]].map(Number);
  return '#' + [r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('');
}
function rgbaFromHexAlpha(hex: string, alpha: number): string {
  if (!hex?.startsWith('#')) return `rgba(0, 0, 0, ${alpha})`;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  if ([r, g, b].some(isNaN)) return `rgba(0, 0, 0, ${alpha})`;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// ─── Defaults (Facebook palette) ──────────────────────────────────────────
const DARK_DEFAULTS: CustomVars = {
  accentRgb: '24, 119, 242',
  bgFrom: '#18191a', bgTo: '#242526',
  bgPrimary: '#18191a', textPrimary: '#e4e6eb',
  textSecondary: '#b0b3b8',
  textMuted: '#8a8d91',
  borderColor: '#3a3b3c',
  bgTertiary: '#3a3b3c',
  cardBg: '#242526',
  cardBgLight: '#2a2b2c',
  bgSecondary: '#242526',
  shadowColor: 'rgba(0, 0, 0, 0.3)',
  navBg: '#242526',
  glassBlur: 0, cardOpacity: 100, fontSans: 'Segoe UI',
  panelRotate: 10,
};
const LIGHT_DEFAULTS: CustomVars = {
  accentRgb: '24, 119, 242',
  bgFrom: '#f0f2f5', bgTo: '#f0f2f5',
  bgPrimary: '#f0f2f5', textPrimary: '#050505',
  textSecondary: '#65676b',
  textMuted: '#8a8d91',
  borderColor: '#ced0d4',
  bgTertiary: '#e4e6eb',
  cardBg: '#ffffff',
  cardBgLight: '#f7f8fa',
  bgSecondary: '#ffffff',
  shadowColor: 'rgba(0, 0, 0, 0.1)',
  navBg: '#ffffff',
  glassBlur: 0, cardOpacity: 100, fontSans: 'Segoe UI',
  panelRotate: 10,
};
function getDefaults(mode: Theme): CustomVars {
  return mode === 'dark' ? { ...DARK_DEFAULTS } : { ...LIGHT_DEFAULTS };
}
function mergeWithDefaults(vars: Partial<CustomVars> | null | undefined, mode: Theme): CustomVars {
  return { ...getDefaults(mode), ...(vars ?? {}) };
}

// ─── Presets ──────────────────────────────────────────────────────────────
type ColorPreset = Pick<
  CustomVars,
  | 'accentRgb' | 'bgFrom' | 'bgTo' | 'bgPrimary' | 'textPrimary' | 'navBg'
  | 'textSecondary' | 'textMuted' | 'borderColor' | 'bgTertiary' | 'cardBg' | 'cardBgLight' | 'bgSecondary' | 'shadowColor'
  | 'accentGreen' | 'accentYellow' | 'accentRed' | 'accentPurple' | 'accentBlue' | 'accentOrange'
>;

/** Semantic colours a preset may set; cleared on every preset switch so one
 *  preset's green/red doesn't leak into the next. */
const SEMANTIC_KEYS = ['accentGreen', 'accentYellow', 'accentRed', 'accentPurple', 'accentBlue', 'accentOrange'] as const;

/** Blend two #rrggbb colours: t=0 → a, t=1 → b. */
function mix(a: string, b: string, t: number): string {
  const p = (h: string) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
  const [x, y] = [p(a), p(b)];
  return '#' + x.map((v, i) => Math.round(v + (y[i] - v) * t).toString(16).padStart(2, '0')).join('');
}

/**
 * A dark preset from its base colours, with the same surface ladder as the
 * default theme (#18191a → #242526 → #3a3b3c): panels, raised surfaces and
 * borders step up from the page towards the text colour. The old presets set
 * panel = page colour, so cards melted into the background.
 */
function darkPreset(accentRgb: string, bg: string, text: string, text2: string): ColorPreset {
  const panel = mix(bg, text, 0.06);
  return {
    accentRgb, bgPrimary: bg, textPrimary: text, textSecondary: text2, textMuted: mix(text2, bg, 0.3),
    bgFrom: bg, bgTo: panel, bgSecondary: panel, cardBg: panel, navBg: panel,
    cardBgLight: mix(bg, text, 0.08), bgTertiary: mix(bg, text, 0.14), borderColor: mix(bg, text, 0.14),
    shadowColor: 'rgba(0, 0, 0, 0.3)',
  };
}

/** A light preset: tinted page, white panels (as in the default light theme). */
function lightPreset(accentRgb: string, bg: string, text: string, text2: string): ColorPreset {
  return {
    accentRgb, bgPrimary: bg, textPrimary: text, textSecondary: text2, textMuted: mix(text2, bg, 0.35),
    bgFrom: bg, bgTo: bg, bgSecondary: '#ffffff', cardBg: '#ffffff', navBg: '#ffffff',
    cardBgLight: mix('#ffffff', bg, 0.5), bgTertiary: mix(bg, text, 0.05), borderColor: mix(bg, text, 0.14),
    shadowColor: 'rgba(0, 0, 0, 0.1)',
  };
}

/** "Old" — the earlier ИЛ ТОД palette (navy page, flag blue, softer semantic hues). */
const OLD_DARK: ColorPreset = {
  accentRgb: '61, 139, 245',
  bgFrom: '#0c0f16', bgTo: '#131722', bgPrimary: '#0c0f16', bgSecondary: '#131722', bgTertiary: '#1c2230',
  cardBg: '#131722', cardBgLight: '#1c2230', navBg: '#131722',
  textPrimary: '#eceef3', textSecondary: '#a7afbf', textMuted: '#6f7789', borderColor: '#283042',
  shadowColor: 'rgba(0, 0, 0, 0.5)',
  accentGreen: '#34c77b', accentYellow: '#f2b705', accentRed: '#f0556b', accentPurple: '#a182e0',
  accentBlue: '#3d8bf5', accentOrange: '#f08a3c',
};
const OLD_LIGHT: ColorPreset = {
  accentRgb: '15, 79, 163',
  bgFrom: '#f4f2ec', bgTo: '#f4f2ec', bgPrimary: '#f4f2ec', bgSecondary: '#ffffff', bgTertiary: '#ebe8df',
  cardBg: '#ffffff', cardBgLight: '#faf9f5', navBg: '#ffffff',
  textPrimary: '#1a1c22', textSecondary: '#50535e', textMuted: '#8b8f9a', borderColor: '#e2decf',
  shadowColor: 'rgba(26, 28, 34, 0.1)',
  accentGreen: '#1a7d4b', accentYellow: '#b5860b', accentRed: '#c0202e', accentPurple: '#6d4aa8',
  accentBlue: '#0f4fa3', accentOrange: '#c2620e',
};

const DARK_PRESETS: { name: string; vars: ColorPreset }[] = [
  { name: 'Ocean',   vars: darkPreset('14, 165, 233', '#021526', '#c8dce8', '#90b0c4') },
  { name: 'Violet',  vars: darkPreset('139, 92, 246', '#130826', '#d4c4f8', '#a89bc8') },
  { name: 'Emerald', vars: darkPreset('34, 197, 94',  '#04170d', '#b8f0cc', '#88bc9c') },
  { name: 'Amber',   vars: darkPreset('249, 115, 22', '#1a0d04', '#fddcb0', '#c8a880') },
  { name: 'Rose',    vars: darkPreset('244, 63, 94',  '#1a0610', '#fcc0cc', '#c49098') },
  { name: 'Old',     vars: OLD_DARK },
];
const LIGHT_PRESETS: { name: string; vars: ColorPreset }[] = [
  { name: 'Old',      vars: OLD_LIGHT },
  { name: 'Warm',     vars: lightPreset('120, 53, 15', '#fbf3e4', '#1c1409', '#78603a') },
  { name: 'Slate',    vars: lightPreset('51, 65, 85',  '#e8edf3', '#0f172a', '#475569') },
  { name: 'Mint',     vars: lightPreset('6, 78, 59',   '#e3f7ec', '#064e3b', '#3d8063') },
  { name: 'Lavender', vars: lightPreset('76, 29, 149', '#efecfb', '#2e1065', '#5e4d8c') },
  { name: 'Rose',     vars: lightPreset('136, 19, 55', '#fcecee', '#881337', '#885567') },
];

const FONTS = [
  { value: 'Space Grotesk', label: 'Space Grotesk' },
  { value: 'Inter',         label: 'Inter' },
  { value: 'DM Sans',       label: 'DM Sans' },
  { value: 'Plus Jakarta Sans', label: 'Jakarta Sans' },
  { value: 'Outfit',        label: 'Outfit' },
  { value: 'Geist',         label: 'Geist' },
];

// ─── Sub-components ───────────────────────────────────────────────────────
function ColorRow({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  const safeVal = value?.startsWith('#') ? value : '#000000';
  return (
    <div className="te-control">
      <span className="te-control-label">{label}</span>
      <div className="te-control-right">
        <label className="te-swatch-wrap" style={{ background: safeVal }}>
          <input type="color" value={safeVal} onChange={(e) => onChange(e.target.value)} className="te-color-input" />
        </label>
        <span className="te-hex-val">{safeVal}</span>
      </div>
    </div>
  );
}

function SliderRow({ label, value, min, max, unit, onChange }: {
  label: string; value: number; min: number; max: number; unit: string;
  onChange: (v: number) => void;
}) {
  const safeVal = value ?? min;
  return (
    <div className="te-control">
      <span className="te-control-label">{label}</span>
      <div className="te-control-right te-slider-row">
        <input type="range" min={min} max={max} value={safeVal}
          onChange={(e) => onChange(Number(e.target.value))} className="te-slider" />
        <span className="te-slider-val">{safeVal}{unit}</span>
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────
type ContentTab = 'colors' | 'appearance';

const ThemeEditor: React.FC = () => {
  const {
    customVars,
    setCustomVars,
    themeEditorOpen,
    setThemeEditorOpen,
    theme: activeTheme,
  } = useUIStore();
  const [editMode, setEditMode]     = useState<Theme>(activeTheme);
  const [contentTab, setContentTab] = useState<ContentTab>('colors');
  const [drafts, setDrafts] = useState<Record<Theme, CustomVars>>({
    dark:  mergeWithDefaults(customVars.dark,  'dark'),
    light: mergeWithDefaults(customVars.light, 'light'),
  });
  const [saved, setSaved] = useState(false);

  const panelRef = useRef<HTMLDivElement>(null);
  const dragOffset = useRef({ x: 0, y: 0 });

  const onDragStart = (e: React.MouseEvent) => {
    const panel = panelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    dragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    panel.style.transform = 'none';

    const move = (ev: MouseEvent) => {
      const x = Math.max(0, Math.min(window.innerWidth - panel.offsetWidth, ev.clientX - dragOffset.current.x));
      const y = Math.max(0, Math.min(window.innerHeight - panel.offsetHeight, ev.clientY - dragOffset.current.y));
      panel.style.left = `${x}px`;
      panel.style.top  = `${y}px`;
    };
    const up = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  useEffect(() => {
    if (themeEditorOpen) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setEditMode(activeTheme);
      setContentTab('colors');
      setDrafts({
        dark:  mergeWithDefaults(customVars.dark,  'dark'),
        light: mergeWithDefaults(customVars.light, 'light'),
      });
      setSaved(false);
    }
  }, [themeEditorOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateDraft = (field: keyof CustomVars, value: string | number) => {
    setDrafts((prev) => {
      const next = { ...prev, [editMode]: { ...prev[editMode], [field]: value } };
      if (editMode === activeTheme) applyCustomVars(next[editMode], editMode);
      return next;
    });
  };

  const applyPreset = (colorVars: ColorPreset) => {
    setDrafts((prev) => {
      const base: CustomVars = { ...prev[editMode] };
      SEMANTIC_KEYS.forEach((k) => { delete base[k]; });
      const next = { ...prev, [editMode]: { ...base, ...colorVars } };
      if (editMode === activeTheme) applyCustomVars(next[editMode], editMode);
      return next;
    });
  };

  const handleSave = () => {
    setCustomVars('dark',  drafts.dark);
    setCustomVars('light', drafts.light);
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  };

  // "Анхны утга" = no overrides at all: clearing them restores the stylesheet's
  // own colours exactly (writing the defaults back as overrides drifted from them).
  const handleReset = () => {
    setDrafts((prev) => ({ ...prev, [editMode]: getDefaults(editMode) }));
    if (editMode === activeTheme) applyCustomVars(null, editMode);
    setCustomVars(editMode, null);
  };

  const handleClose = () => {
    const current = activeTheme === 'dark' ? customVars.dark : customVars.light;
    applyCustomVars(current, activeTheme);
    setThemeEditorOpen(false);
  };

  if (!themeEditorOpen) return null;

  const draft   = drafts[editMode];
  const isLive  = editMode === activeTheme;
  const presets = editMode === 'dark' ? DARK_PRESETS : LIGHT_PRESETS;

  return createPortal(
    <div className="te-overlay" onClick={handleClose}>
      <div className="te-panel" ref={panelRef} onClick={(e) => e.stopPropagation()}>

        {/* Header — drag handle */}
        <div className="te-header te-header--draggable" onMouseDown={onDragStart}>
          <span className="te-title">Дизайн тохируулах</span>
          <button className="te-close" onMouseDown={(e) => e.stopPropagation()} onClick={handleClose}><X size={14} /></button>
        </div>

        {/* Row: dark/light mode + preview badge */}
        <div className="te-mode-row">
          <div className="te-tabs te-tabs-mode">
            <button className={`te-tab ${editMode === 'dark'  ? 'active' : ''}`} onClick={() => setEditMode('dark')}>
              <Moon size={11} /> Бараан {activeTheme === 'dark'  && <span className="te-tab-badge">•</span>}
            </button>
            <button className={`te-tab ${editMode === 'light' ? 'active' : ''}`} onClick={() => setEditMode('light')}>
              <Sun  size={11} /> Цайвар {activeTheme === 'light' && <span className="te-tab-badge">•</span>}
            </button>
          </div>
          {!isLive && <span className="te-no-preview">preview байхгүй</span>}
        </div>

        {/* Content tabs */}
        <div className="te-tabs te-tabs-content">
          <button className={`te-tab ${contentTab === 'colors' ? 'active' : ''}`} onClick={() => setContentTab('colors')}>
            <Palette size={11} /> Өнгө
          </button>
          <button className={`te-tab ${contentTab === 'appearance' ? 'active' : ''}`} onClick={() => setContentTab('appearance')}>
            <Sliders size={11} /> Харагдац
          </button>
        </div>

        {/* ── Colors tab ── */}
        {contentTab === 'colors' && (
          <>
            <div className="te-section">
              <div className="te-section-label">Урьдчилан тохиргоо</div>
              <div className="te-presets">
                {presets.map((p) => (
                  <button key={p.name} className="te-preset"
                    style={{ background: `linear-gradient(135deg, ${p.vars.bgFrom}, ${p.vars.bgTo})` }}
                    onClick={() => applyPreset(p.vars)} title={p.name}>
                    <span className="te-preset-dot" style={{ background: `rgb(${p.vars.accentRgb})` }} />
                    <span className="te-preset-name">{p.name}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="te-section">
              <div className="te-control">
                <span className="te-control-label">Accent</span>
                <div className="te-control-right">
                  <label className="te-swatch-wrap" style={{ background: `rgb(${draft.accentRgb})` }}>
                    <input type="color" value={rgbToHex(draft.accentRgb)}
                      onChange={(e) => updateDraft('accentRgb', hexToRgb(e.target.value))} className="te-color-input" />
                  </label>
                  <span className="te-hex-val">rgb({draft.accentRgb})</span>
                </div>
              </div>

              <div className="te-control">
                <span className="te-control-label">Градиент</span>
                <div className="te-control-right">
                  <label className="te-swatch-wrap" style={{ background: draft.bgFrom }}>
                    <input type="color" value={draft.bgFrom ?? '#000000'}
                      onChange={(e) => updateDraft('bgFrom', e.target.value)} className="te-color-input" />
                  </label>
                  <span className="te-arrow">→</span>
                  <label className="te-swatch-wrap" style={{ background: draft.bgTo }}>
                    <input type="color" value={draft.bgTo ?? '#000000'}
                      onChange={(e) => updateDraft('bgTo', e.target.value)} className="te-color-input" />
                  </label>
                  <div className="te-gradient-strip"
                    style={{ background: `linear-gradient(to right, ${draft.bgFrom}, ${draft.bgTo})` }} />
                </div>
              </div>

              <ColorRow label="Фон"          value={draft.bgPrimary}     onChange={(v) => updateDraft('bgPrimary', v)} />
              <ColorRow label="Үсэг"         value={draft.textPrimary}   onChange={(v) => updateDraft('textPrimary', v)} />
              <ColorRow label="2-р үсэг"     value={draft.textSecondary ?? (editMode === 'dark' ? '#b0b3b8' : '#65676b')} onChange={(v) => updateDraft('textSecondary', v)} />
              <ColorRow label="Бүдэг үсэг"   value={draft.textMuted ?? '#8a8d91'} onChange={(v) => updateDraft('textMuted', v)} />
              <ColorRow label="Хүрээ"        value={draft.borderColor ?? (editMode === 'dark' ? '#3a3b3c' : '#ced0d4')} onChange={(v) => updateDraft('borderColor', v)} />
              <ColorRow label="Card фон"     value={draft.cardBg ?? (editMode === 'dark' ? '#242526' : '#ffffff')} onChange={(v) => updateDraft('cardBg', v)} />
              <ColorRow label="Card хоёрдогч" value={draft.cardBgLight ?? (editMode === 'dark' ? '#2a2b2c' : '#f7f8fa')} onChange={(v) => updateDraft('cardBgLight', v)} />
              <ColorRow label="2-р фон"      value={draft.bgSecondary ?? (editMode === 'dark' ? '#242526' : '#ffffff')} onChange={(v) => updateDraft('bgSecondary', v)} />
              <ColorRow label="Дэд фон"      value={draft.bgTertiary ?? (editMode === 'dark' ? '#3a3b3c' : '#e4e6eb')} onChange={(v) => updateDraft('bgTertiary', v)} />
              <ColorRow label="Сүүдэр"       value={asHexFromRgba(draft.shadowColor) ?? (editMode === 'dark' ? '#000000' : '#000000')} onChange={(v) => updateDraft('shadowColor', rgbaFromHexAlpha(v, editMode === 'dark' ? 0.3 : 0.1))} />
              <ColorRow label="Sidebar"      value={draft.navBg}         onChange={(v) => updateDraft('navBg', v)} />
            </div>

            <div className="te-section">
              <div className="te-section-label">Семантик өнгө</div>
              <div className="te-semantic-row">
                {([
                  { key: 'accentGreen',  label: 'Ногоон', dk: '#22c55e', lt: '#16a34a' },
                  { key: 'accentYellow', label: 'Шар',    dk: '#eab308', lt: '#ca8a04' },
                  { key: 'accentRed',    label: 'Улаан',  dk: '#ef4444', lt: '#dc2626' },
                  { key: 'accentPurple', label: 'Ягаан',  dk: '#8b5cf6', lt: '#7c3aed' },
                  { key: 'accentBlue',   label: 'Цэнхэр', dk: '#3b82f6', lt: '#2563eb' },
                  { key: 'accentOrange', label: 'Улбар',  dk: '#f97316', lt: '#ea580c' },
                ] as const).map(({ key, label, dk, lt }) => {
                  const fallback = editMode === 'dark' ? dk : lt;
                  const val = (draft[key] as string | undefined) ?? fallback;
                  return (
                    <div key={key} className="te-semantic-item" title={label}>
                      <label className="te-swatch-wrap te-swatch-lg" style={{ background: val }}>
                        <input type="color" value={val} className="te-color-input"
                          onChange={(e) => updateDraft(key, e.target.value)} />
                      </label>
                      <span className="te-semantic-label">{label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}

        {/* ── Appearance tab ── */}
        {contentTab === 'appearance' && (
          <div className="te-section">
            <SliderRow label="Blur" value={draft.glassBlur} min={0} max={28} unit="px"
              onChange={(v) => updateDraft('glassBlur', v)} />
            <SliderRow
              label={editMode === 'dark' ? 'Card тунгалагт' : 'Card дэвсгэр'}
              value={draft.cardOpacity} min={0} max={editMode === 'dark' ? 20 : 100} unit="%"
              onChange={(v) => updateDraft('cardOpacity', v)} />
            <SliderRow label="Панел эргэлт" value={draft.panelRotate ?? 10} min={0} max={25} unit="°"
              onChange={(v) => updateDraft('panelRotate', v)} />
            <div className="te-control">
              <span className="te-control-label">Фонт</span>
              <select className="te-select" value={draft.fontSans ?? 'Space Grotesk'}
                onChange={(e) => updateDraft('fontSans', e.target.value)}>
                {FONTS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
            </div>
            <div className="te-control">
              <span className="te-control-label">Background</span>
              <span className="te-hex-val">Color only</span>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="te-actions">
          <button className="te-btn te-btn-reset" onClick={handleReset}>
            <RotateCcw size={12} /> Анхны утга
          </button>
          <button className={`te-btn te-btn-save ${saved ? 'saved' : ''}`} onClick={handleSave}>
            <Check size={12} /> {saved ? 'Хадгалагдлаа' : 'Хадгалах'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ThemeEditor;
